import { useState } from 'react';
import { CpmButton, CpmModal, CpmTypeChip } from '../../../../components/design-system';
import {
    formatCreatedVia,
    formatFlaggedDate,
    packLabel,
    recordTypeLabel,
} from '../../../../constants/centralProductMaster.constants';
import { approveQueueItem } from '../../../../services/productMasterService';
import type { CpmPendingItem } from '../../../../types/centralProductMaster.types';

interface QuickReviewModalProps {
    open: boolean;
    item: CpmPendingItem | null;
    onClose: () => void;
    onOpenFull: (item: CpmPendingItem) => void;
    onApproved: () => void;
}

type Phase = 'review' | 'working' | 'error' | 'done';

export default function QuickReviewModal({
    open,
    item,
    onClose,
    onOpenFull,
    onApproved,
}: QuickReviewModalProps) {
    const [phase, setPhase] = useState<Phase>('review');

    if (!open || !item) return null;

    const pack = packLabel(item);
    const brand =
        item.level === 'brand'
            ? item.name
            : item.brand?.name ||
              (item.parent?.level === 'brand' ? item.parent.name : null) ||
              '—';

    const fields: Array<{ label: string; value: string; wide?: boolean }> = [
        { label: 'Brand', value: brand },
        { label: 'Volume', value: pack ?? '—' },
        {
            label: 'Created by',
            value: formatCreatedVia(item.provenance?.createdBy),
            wide: true,
        },
        { label: 'Record type', value: recordTypeLabel(item.recordType) },
        { label: 'Created on', value: formatFlaggedDate(item.createdAt) },
    ];

    const approve = async () => {
        setPhase('working');
        try {
            await approveQueueItem({ level: item.level, masterId: item._id });
            setPhase('done');
            onApproved();
        } catch {
            setPhase('error');
        }
    };

    const close = () => {
        setPhase('review');
        onClose();
    };

    return (
        <CpmModal open={open} onClose={close} maxWidth={460} zIndex={60}>
            {phase === 'review' ? (
                <>
                    <div
                        className="flex items-start border-b border-[var(--cpm-border-default)]"
                        style={{ gap: 13, padding: '20px 22px 16px' }}
                    >
                        <span
                            className="shrink-0 inline-flex items-center justify-center bg-[var(--cpm-purple-tint-13)] text-[var(--cpm-brand-purple)]"
                            style={{ width: 38, height: 38, borderRadius: 10 }}
                        >
                            <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <path d="M14 2v6h6" />
                            </svg>
                        </span>
                        <div className="flex-1 min-w-0 flex flex-col" style={{ gap: 6 }}>
                            <div className="flex items-center flex-wrap" style={{ gap: 9 }}>
                                <span
                                    className="inline-flex items-center rounded-full bg-[var(--cpm-success-bg)] border border-[var(--cpm-success-border)] text-[var(--cpm-success-text-light)] uppercase"
                                    style={{
                                        padding: '3px 10px',
                                        fontSize: 10,
                                        fontWeight: 800,
                                        letterSpacing: '0.05em',
                                    }}
                                >
                                    Quick review
                                </span>
                                <CpmTypeChip recordType={item.recordType} />
                            </div>
                            <h2 className="m-0 !text-[18px] !font-extrabold !tracking-[-0.02em] !leading-[1.2] !text-[var(--cpm-brand-navy)]">
                                {item.name}
                            </h2>
                        </div>
                        <button
                            type="button"
                            onClick={close}
                            className="shrink-0 inline-flex items-center justify-center bg-white border-[1.5px] border-[var(--cpm-border-strong)] text-[var(--cpm-text-secondary)] hover:border-[var(--cpm-brand-purple)] hover:text-[var(--cpm-brand-navy)]"
                            style={{ width: 30, height: 30, borderRadius: 8 }}
                        >
                            <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.4"
                            >
                                <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
                            </svg>
                        </button>
                    </div>

                    <div className="flex flex-col" style={{ padding: '18px 22px 20px', gap: 14 }}>
                        <div className="grid grid-cols-2" style={{ gap: 10 }}>
                            {fields.map((f) => (
                                <div
                                    key={f.label}
                                    className="flex flex-col bg-[var(--cpm-surface-subtle)] border border-[var(--cpm-border-default)]"
                                    style={{
                                        gap: 4,
                                        padding: '11px 13px',
                                        borderRadius: 10,
                                        gridColumn: f.wide ? '1 / -1' : undefined,
                                    }}
                                >
                                    <span
                                        className="uppercase text-[var(--cpm-text-tertiary)]"
                                        style={{
                                            fontSize: 10,
                                            fontWeight: 800,
                                            letterSpacing: '0.07em',
                                        }}
                                    >
                                        {f.label}
                                    </span>
                                    <span
                                        className="text-[var(--cpm-text-primary)]"
                                        style={{ fontSize: 13.5, fontWeight: 700 }}
                                    >
                                        {f.value}
                                    </span>
                                </div>
                            ))}
                        </div>
                        {item.possibleDuplicateOf ? (
                            <div
                                className="flex bg-[var(--cpm-warning-bg)] border border-[var(--cpm-warning-border)]"
                                style={{ gap: 11, padding: '12px 14px', borderRadius: 10 }}
                            >
                                <svg
                                    width="15"
                                    height="15"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="var(--cpm-warning-text)"
                                    strokeWidth="2"
                                    className="shrink-0 mt-px"
                                >
                                    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                                    <path d="M12 9v4" />
                                    <path d="M12 17h.01" />
                                </svg>
                                <span
                                    className="text-[var(--cpm-warning-text)]"
                                    style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.5 }}
                                >
                                    Possible duplicate of &ldquo;
                                    {item.possibleDuplicate?.name || 'another record'}&rdquo;.
                                    Open the full record to compare before approving.
                                </span>
                            </div>
                        ) : (
                            <div
                                className="flex bg-[var(--cpm-success-bg)] border border-[var(--cpm-success-border)]"
                                style={{ gap: 11, padding: '12px 14px', borderRadius: 10 }}
                            >
                                <svg
                                    width="15"
                                    height="15"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="var(--cpm-success-text-light)"
                                    strokeWidth="2"
                                    className="shrink-0 mt-px"
                                >
                                    <path
                                        d="M20 6 9 17l-5-5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <span
                                    className="text-[var(--cpm-success-text-light)]"
                                    style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.5 }}
                                >
                                    No possible-duplicate flag on this record — there is nothing to
                                    compare it against. Confirm the name, brand and volume read
                                    correctly, then approve it straight into the catalog.
                                </span>
                            </div>
                        )}
                    </div>

                    <div
                        className="flex items-center border-t-[1.5px] border-[var(--cpm-border-strong)]"
                        style={{
                            gap: 12,
                            padding: '14px 22px',
                            background: 'rgba(249,249,252,0.88)',
                        }}
                    >
                        <button
                            type="button"
                            onClick={() => {
                                onOpenFull(item);
                                close();
                            }}
                            className="text-[var(--cpm-brand-purple)] border-b border-[var(--cpm-brand-purple)]"
                            style={{ fontSize: 12.5, fontWeight: 800 }}
                        >
                            Open full record
                        </button>
                        <div className="ml-auto flex items-center" style={{ gap: 10 }}>
                            <CpmButton variant="outline" size="modal" onClick={close}>
                                Cancel
                            </CpmButton>
                            <CpmButton
                                variant="navy"
                                size="modal"
                                onClick={approve}
                                leadingIcon={
                                    <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.4"
                                    >
                                        <path
                                            d="M20 6 9 17l-5-5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                }
                            >
                                Approve record
                            </CpmButton>
                        </div>
                    </div>
                </>
            ) : null}

            {phase === 'working' ? (
                <div
                    className="flex flex-col items-center text-center"
                    style={{ padding: '40px 30px 34px', gap: 14 }}
                >
                    <span className="cpm-spin" style={{ width: 34, height: 34, borderWidth: 3.4 }} />
                    <span
                        className="text-[var(--cpm-brand-navy)]"
                        style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.01em' }}
                    >
                        Promoting record…
                    </span>
                    <span
                        className="text-[var(--cpm-text-secondary)] max-w-[330px]"
                        style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.5 }}
                    >
                        Writing the record to the active catalog.
                    </span>
                </div>
            ) : null}

            {phase === 'error' ? (
                <>
                    <div
                        className="flex flex-col items-center text-center"
                        style={{ padding: '34px 30px 22px', gap: 12 }}
                    >
                        <span
                            className="inline-flex items-center justify-center bg-[var(--cpm-shortfall-bg)] border border-[var(--cpm-shortfall-border)] text-[var(--cpm-shortfall-text)]"
                            style={{ width: 52, height: 52, borderRadius: 14 }}
                        >
                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                                <path d="M12 9v4" />
                                <path d="M12 17h.01" />
                            </svg>
                        </span>
                        <span
                            className="text-[var(--cpm-brand-navy)]"
                            style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.02em' }}
                        >
                            Promotion failed
                        </span>
                        <span
                            className="text-[var(--cpm-text-secondary)] max-w-[340px]"
                            style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.5 }}
                        >
                            The record was not written to the active catalog. It stays in the queue.
                            Try again.
                        </span>
                    </div>
                    <div
                        className="flex items-center border-t-[1.5px] border-[var(--cpm-border-strong)]"
                        style={{
                            gap: 10,
                            padding: '14px 22px',
                            background: 'rgba(249,249,252,0.88)',
                        }}
                    >
                        <div className="ml-auto flex items-center" style={{ gap: 10 }}>
                            <CpmButton variant="outline" size="modal" onClick={close}>
                                Close
                            </CpmButton>
                            <CpmButton
                                variant="navy"
                                size="modal"
                                onClick={approve}
                                leadingIcon={
                                    <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.4"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                                        <path d="M3 3v5h5" />
                                    </svg>
                                }
                            >
                                Try again
                            </CpmButton>
                        </div>
                    </div>
                </>
            ) : null}

            {phase === 'done' ? (
                <div
                    className="flex flex-col items-center text-center"
                    style={{ padding: '40px 30px 30px', gap: 12 }}
                >
                    <span
                        className="inline-flex items-center justify-center bg-[var(--cpm-success-bg)] border border-[var(--cpm-success-border)] text-[var(--cpm-success-text-light)]"
                        style={{ width: 52, height: 52, borderRadius: 14 }}
                    >
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                        >
                            <path
                                d="M20 6 9 17l-5-5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </span>
                    <span
                        className="text-[var(--cpm-brand-navy)]"
                        style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.02em' }}
                    >
                        Promoted to Active
                    </span>
                    <span
                        className="text-[var(--cpm-text-secondary)]"
                        style={{
                            fontSize: 12.5,
                            fontWeight: 600,
                            lineHeight: 1.5,
                            maxWidth: 340,
                        }}
                    >
                        “{item.name}” is now part of the active catalog and visible to every store
                        brand. It stays in this list marked as approved until you refresh.
                    </span>
                    <CpmButton variant="navy" size="md" className="mt-1.5" onClick={close}>
                        Back to the queue
                    </CpmButton>
                </div>
            ) : null}
        </CpmModal>
    );
}
